import React, { useState, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api, apiErrorMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const CONDITIONS = [
  { value: "like_new", label: "Like New" },
  { value: "excellent", label: "Excellent" },
  { value: "very_good", label: "Very Good" },
  { value: "good", label: "Good" },
  { value: "fair", label: "Fair" },
  { value: "damaged", label: "Damaged" },
];

const inputCls = "bg-white border border-zinc-200 px-3 py-2.5 text-sm text-zinc-900 placeholder:text-zinc-400 rounded-sm focus:border-teal-600 outline-none";

export default function SellToLookers() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const [form, setForm] = useState({
    brand: "", model: "", frame_colour: "", approximate_age: "", original_purchase_price: "",
    case_included: false, receipt_available: false, prescription_fitted: false,
    damage_description: "", description: "", condition_self: "excellent", phone: "",
  });
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  if (!user) {
    return (
      <div className="container-lookers py-24 text-center" data-testid="sell-login-required">
        <h1 className="font-serif text-3xl mb-4 text-zinc-900">
          Log in to sell your frames
        </h1>
        <p className="text-sm text-zinc-500 mb-6">Create a free account so we can keep you updated on your offer.</p>
        <Link to="/login" state={{ from: { pathname: "/sell" } }} className="bg-teal-600 hover:bg-teal-700 text-ink px-6 py-3 text-xs font-heading font-semibold uppercase rounded-full transition-colors">Log In</Link>
      </div>
    );
  }

  const onPhotoUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/uploads/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setPhotos((prev) => [...prev, data.url]);
      }
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setUploading(false);
    }
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.brand || !form.frame_colour) {
      toast.error("Please provide at least the brand and frame colour");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/seller-submissions", { ...form, photos, original_purchase_price: form.original_purchase_price ? Number(form.original_purchase_price) : null });
      toast.success(`Submission ${data.submission_number} received`);
      navigate("/account/submissions");
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="container-lookers py-12 max-w-2xl" data-testid="sell-to-lookers-page">
      <h1 className="font-serif text-4xl mb-3 text-zinc-900">
        Your Old Frames Could Be Someone Else's Next Look.
      </h1>
      <p className="text-sm text-zinc-500 mb-10">Have designer glasses sitting in a drawer? Give them another look. Send us a few photos and some basic information and we'll tell you what we may be prepared to pay for them. Lookers conducts a final physical assessment before any offer is confirmed.</p>

      <form onSubmit={onSubmit} className="space-y-6">
        <div className="grid grid-cols-2 gap-3">
          <input required placeholder="Brand (e.g. Ray-Ban)" value={form.brand} onChange={(e) => setForm({ ...form, brand: e.target.value })} className={inputCls} data-testid="sell-brand-input" />
          <input placeholder="Model (if known)" value={form.model} onChange={(e) => setForm({ ...form, model: e.target.value })} className={inputCls} data-testid="sell-model-input" />
          <input required placeholder="Frame colour" value={form.frame_colour} onChange={(e) => setForm({ ...form, frame_colour: e.target.value })} className={inputCls} data-testid="sell-colour-input" />
          <input placeholder="Approximate age (e.g. 2 years)" value={form.approximate_age} onChange={(e) => setForm({ ...form, approximate_age: e.target.value })} className={inputCls} data-testid="sell-age-input" />
          <input type="number" placeholder="Original purchase price" value={form.original_purchase_price} onChange={(e) => setForm({ ...form, original_purchase_price: e.target.value })} className={inputCls} data-testid="sell-price-input" />
          <input placeholder="Phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className={inputCls} data-testid="sell-phone-input" />
        </div>

        <div className="flex flex-wrap gap-4 text-zinc-800">
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.case_included} onChange={(e) => setForm({ ...form, case_included: e.target.checked })} className="accent-teal-600" data-testid="sell-case-checkbox" /> Case included</label>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.receipt_available} onChange={(e) => setForm({ ...form, receipt_available: e.target.checked })} className="accent-teal-600" data-testid="sell-receipt-checkbox" /> Receipt available</label>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.prescription_fitted} onChange={(e) => setForm({ ...form, prescription_fitted: e.target.checked })} className="accent-teal-600" data-testid="sell-prescription-checkbox" /> Prescription lenses fitted</label>
        </div>

        <div>
          <label className="text-xs font-heading uppercase tracking-wide mb-2 block text-teal-700">Approximate condition</label>
          <select value={form.condition_self} onChange={(e) => setForm({ ...form, condition_self: e.target.value })} className={`${inputCls} w-full`} data-testid="sell-condition-select">
            {CONDITIONS.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
          </select>
        </div>

        <textarea placeholder="Any damage?" value={form.damage_description} onChange={(e) => setForm({ ...form, damage_description: e.target.value })} className={`${inputCls} w-full`} rows={2} data-testid="sell-damage-input" />
        <textarea placeholder="Anything else we should know?" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className={`${inputCls} w-full`} rows={3} data-testid="sell-description-input" />

        <div>
          <label className="text-xs font-heading uppercase tracking-wide mb-2 block text-teal-700">Photos — front, left, right, folded, model number, any damage</label>
          <input ref={fileRef} type="file" accept="image/*" multiple capture="environment" onChange={onPhotoUpload} className="text-sm text-zinc-800" data-testid="sell-photo-input" />
          {uploading && <p className="text-xs text-zinc-400 mt-2">Uploading…</p>}
          <div className="flex gap-2 mt-3 flex-wrap">
            {photos.map((url) => <img key={url} src={`${process.env.REACT_APP_BACKEND_URL}${url}`} alt="" className="w-16 h-16 object-cover rounded-sm border border-zinc-200" />)}
          </div>
        </div>

        <button type="submit" disabled={submitting} className="bg-teal-600 hover:bg-teal-700 text-ink font-heading font-semibold text-xs uppercase tracking-wide py-4 px-8 rounded-full transition-colors disabled:opacity-50" data-testid="sell-submit-button">
          {submitting ? "Submitting…" : "Submit For Review"}
        </button>
      </form>
    </div>
  );
}
